import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { intentsApi, type CreateIntentPayload, type IntentRule } from '@/api/intents';
import { Button, EmptyState, Input, Loading, Modal } from '@/components/ui';

interface Props {
  orgId: string;
}

interface IntentFormState {
  name: string;
  label: string;
  description: string;
  examplesText: string;
  enabled: boolean;
}

const emptyForm: IntentFormState = {
  name: '',
  label: '',
  description: '',
  examplesText: '',
  enabled: true,
};

function parseExamples(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function buildPayload(orgId: string, form: IntentFormState): CreateIntentPayload {
  return {
    org_id: orgId,
    name: form.name.trim(),
    label: form.label.trim(),
    description: form.description.trim(),
    examples: parseExamples(form.examplesText),
    enabled: form.enabled,
  };
}

function IntentCard({
  rule,
  onToggle,
  onDelete,
  isToggling,
  isDeleting,
}: {
  rule: IntentRule;
  onToggle: (rule: IntentRule) => void;
  onDelete: (rule: IntentRule) => void;
  isToggling: boolean;
  isDeleting: boolean;
}) {
  return (
    <div className="rounded-lg border border-navy-border bg-slate-800/50 p-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-base font-semibold text-white">{rule.label}</h3>
            <span className="rounded bg-indigo-500/10 px-2 py-0.5 font-mono text-xs text-indigo-300">
              {rule.name}
            </span>
            <span className={rule.enabled ? 'text-xs text-emerald-300' : 'text-xs text-gray-400'}>
              {rule.enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>

          <p className="mt-2 text-sm leading-6 text-gray-400">{rule.description}</p>

          {rule.examples.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {rule.examples.map((example) => (
                <span
                  key={example}
                  className="max-w-full truncate rounded border border-navy-border bg-navy/50 px-2 py-1 text-xs text-gray-300"
                >
                  {example}
                </span>
              ))}
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={isToggling}
            onClick={() => onToggle(rule)}
          >
            {rule.enabled ? 'Disable' : 'Enable'}
          </Button>
          <Button
            type="button"
            variant="danger"
            size="sm"
            loading={isDeleting}
            onClick={() => onDelete(rule)}
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function OrgIntentsTab({ orgId }: Props) {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState<IntentFormState>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const queryKey = useMemo(() => ['org-intents', orgId], [orgId]);

  const { data: rules = [], isLoading } = useQuery({
    queryKey,
    queryFn: () => intentsApi.list(orgId),
  });

  useEffect(() => {
    if (!isModalOpen) return;
    setForm(emptyForm);
    setErrors({});
  }, [isModalOpen]);

  const createMutation = useMutation({
    mutationFn: (payload: CreateIntentPayload) => intentsApi.create(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      toast.success('Intent rule created');
      setIsModalOpen(false);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to create intent rule');
    },
  });

  const toggleMutation = useMutation({
    mutationFn: (rule: IntentRule) => intentsApi.update(rule.id, { enabled: !rule.enabled }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      toast.success('Intent rule updated');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update intent rule');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (rule: IntentRule) => intentsApi.remove(rule.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
      toast.success('Intent rule deleted');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to delete intent rule');
    },
  });

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    if (!form.name.trim()) nextErrors.name = 'Name is required';
    if (!/^[a-z0-9_]+$/.test(form.name.trim())) {
      nextErrors.name = 'Use lowercase letters, numbers, and underscores only';
    }
    if (!form.label.trim()) nextErrors.label = 'Label is required';
    if (!form.description.trim()) nextErrors.description = 'Description is required';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    createMutation.mutate(buildPayload(orgId, form));
  };

  const handleDelete = (rule: IntentRule) => {
    if (!window.confirm(`Delete intent "${rule.label}"?`)) return;
    deleteMutation.mutate(rule);
  };

  if (isLoading) return <Loading text="Loading intent rules..." />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white">Intent Rules</h2>
          <p className="mt-1 text-sm text-gray-400">
            Manage classifier intents for this organization's WhatsApp messages.
          </p>
        </div>
        <Button type="button" onClick={() => setIsModalOpen(true)}>
          <Plus className="h-4 w-4" />
          Add Intent
        </Button>
      </div>

      {rules.length === 0 ? (
        <EmptyState
          icon={<Bot className="h-8 w-8" />}
          title="No intent rules"
          description="Add intent definitions to classify incoming WhatsApp messages."
          actionLabel="Add Intent"
          onAction={() => setIsModalOpen(true)}
        />
      ) : (
        <div className="space-y-3">
          {rules.map((rule) => (
            <IntentCard
              key={rule.id}
              rule={rule}
              onToggle={(item) => toggleMutation.mutate(item)}
              onDelete={handleDelete}
              isToggling={toggleMutation.isPending && toggleMutation.variables?.id === rule.id}
              isDeleting={deleteMutation.isPending && deleteMutation.variables?.id === rule.id}
            />
          ))}
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Add Intent"
        size="lg"
        footer={(
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsModalOpen(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmit} loading={createMutation.isPending}>
              Save Intent
            </Button>
          </>
        )}
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={form.name}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
            placeholder="interested_in_job"
            error={errors.name}
          />

          <Input
            label="Label"
            value={form.label}
            onChange={(event) => setForm((current) => ({ ...current, label: event.target.value }))}
            placeholder="Interested in job"
            error={errors.label}
          />

          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-secondary">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
              rows={4}
              className="w-full rounded border border-navy-border bg-navy-deeper px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:border-gold/60 focus:outline-none focus:ring-2 focus:ring-gold/40"
              placeholder="When should this intent fire?"
            />
            {errors.description ? <p className="mt-1 text-xs text-danger">{errors.description}</p> : null}
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-ink-secondary">
              Examples
            </label>
            <textarea
              value={form.examplesText}
              onChange={(event) => setForm((current) => ({ ...current, examplesText: event.target.value }))}
              rows={5}
              className="w-full rounded border border-navy-border bg-navy-deeper px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted focus:border-gold/60 focus:outline-none focus:ring-2 focus:ring-gold/40"
              placeholder="One example per line"
            />
          </div>

          <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-navy-border bg-slate-800/50 p-3">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))}
              className="h-4 w-4 rounded border-navy-border bg-navy-deeper text-indigo-500 focus:ring-indigo-500"
            />
            <span className="text-sm font-medium text-white">Enabled</span>
          </label>
        </div>
      </Modal>
    </div>
  );
}
